/**
 * mpVisuals.tsx —— 多人对战共享视觉件（第 16 轮设计还原）。
 *
 *  - `mpAvatarFor`：按「昵称 + 座位」稳定哈希映射到既有 5 套头像资源（D-S2 / AC2.2 无头像）；
 *  - `CrownIcon`：房主标识改为 Crown 图标（D-N3，取 --semantic-winner 金色）；
 *  - `MpSyncOverlay`：断线 / 刷新恢复期的全屏毛玻璃遮罩「正在同步对局状态…」（BUG-R15-04 / D-S5）；
 *  - `MpLeaveConfirm`：对局中离开的二次确认「中途离开将自动弃牌」（BUG-R15-02 / D-S6）。
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { SeatPresence } from '../../net/protocol';
import { setLetwayFloat } from '../../state/letway';
import { STYLE_AVATARS } from '../avatars';

const AVATAR_LIST = Object.values(STYLE_AVATARS);

/** 稳定哈希：同一昵称 + 座位在所有客户端上得到同一张头像。 */
export function mpAvatarFor(name: string | null | undefined, seat: number): string {
  const key = `${name ?? ''}#${seat}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % AVATAR_LIST.length;
  return AVATAR_LIST[idx];
}

export function MpAvatar({ name, seat, size = 40 }: { name: string | null; seat: number; size?: number }) {
  return (
    <img
      className="mp-avatar"
      src={mpAvatarFor(name, seat)}
      alt={name ? `${name} 的头像` : '玩家头像'}
      width={size}
      height={size}
      draggable={false}
    />
  );
}

export function CrownIcon({ title = '房主' }: { title?: string }) {
  return (
    <svg className="mp-crown" viewBox="0 0 24 24" width="16" height="16" role="img" aria-label={title}>
      <title>{title}</title>
      <path
        d="M3 18h18l-1.4-9-4.3 3.4L12 5l-3.3 7.4L4.4 9 3 18z"
        fill="var(--semantic-winner)"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="0.8"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* 席位在线状态徽标（第 19 轮）                                        */
/* ------------------------------------------------------------------ */

/**
 * 席位徽标：区分「掉线中」（宽限期内等待重连，琥珀色 + 呼吸点）与「已离开」（灰、静态）。
 * 老服务端不下发 `presence` 时回落为中性「离线」，不误导用户。
 */
export function PresenceBadge({ connected, presence }: { connected: boolean; presence?: SeatPresence }) {
  if (connected || presence === 'online') return null;
  const kind: 'reconnecting' | 'left' | 'unknown' =
    presence === 'reconnecting' ? 'reconnecting' : presence === 'left' ? 'left' : 'unknown';
  const label = kind === 'reconnecting' ? '掉线中' : kind === 'left' ? '已离开' : '离线';
  const title = kind === 'reconnecting'
    ? '该玩家网络中断，30 秒内重连可继续对局'
    : kind === 'left' ? '该玩家已离开对局' : '该玩家当前不在线';
  return (
    <span className={`mp-badge mp-badge--off mp-badge--${kind}`} title={title} data-presence={kind}>
      {kind === 'reconnecting' && <span className="mp-badge__dot" aria-hidden="true" />}
      {label}
    </span>
  );
}

/** 全屏毛玻璃同步遮罩（设计稿 §4.4）。 */
export function MpSyncOverlay({ text = '正在同步对局状态…' }: { text?: string }) {
  return (
    <div className="mp-sync" role="status" aria-live="polite" data-testid="mp-sync-overlay">
      <div className="mp-sync__box">
        <span className="mp-sync__spinner" aria-hidden="true" />
        <span className="mp-sync__text">{text}</span>
      </div>
    </div>
  );
}

/**
 * 惰性获取（必要时创建）body 根下的多人打断层容器 `#mp-modal-root`。
 * 它与 `.toast-layer` / `.pwa-hint` 同为 body 根兄弟，令 portal 进来的 mp-confirm
 * 逃出 `.mp-root`（position:fixed 独立层叠上下文，根级 z=auto=0），在 body 根上以
 * z=var(--z-mp-confirm)=980 与 toast(115)/pwa(880) 直接可比（FR-E2 · AC-E2.4）。
 */
function ensureMpModalRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById('mp-modal-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'mp-modal-root';
    document.body.appendChild(el);
  }
  return el;
}

/**
 * 对局中离开的二次确认弹窗。
 *
 * 二期 FR-E2：① 通过 React portal 挂到 body 根的 `#mp-modal-root`，使有效层级 980 恒 >
 * toast(115)/pwa(880)，根治「确认框被顶部 toast 压半截」；② 挂载即登记为打断类关键浮层，
 * 触发 toast 静默排队 + pwa 让位 + 禁触发 tooltip/popover（AC-E2.1~E2.4 / AC-E2.5）。
 * DOM 结构（`.mp-confirm` 等选择器）保持不变，portal 后仍可被 diag/测试按已知选择器访问。
 */
export function MpLeaveConfirm({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  const rootRef = useRef<HTMLElement | null>(null);
  if (rootRef.current === null) rootRef.current = ensureMpModalRoot();

  // 打断类关键浮层在场登记（挂载即静默 toast、pwa 让位、禁触发辅助浮层）
  useEffect(() => {
    setLetwayFloat('mp-confirm', true);
    return () => setLetwayFloat('mp-confirm', false);
  }, []);

  const dialog = (
    <div className="mp-confirm" role="dialog" aria-modal="true" aria-label="确认离开对局">
      <div className="mp-confirm__scrim" onClick={onCancel} />
      <div className="mp-confirm__box">
        <h3 className="mp-confirm__title">确认离开当前对局？</h3>
        <p className="mp-confirm__desc">中途离开将自动弃牌，本手已投入的筹码不会退回。</p>
        <div className="mp-confirm__actions">
          <button className="mp-btn mp-btn--ghost" onClick={onCancel}>继续对局</button>
          <button className="mp-btn mp-btn--fold" onClick={onConfirm}>确认离开（弃牌）</button>
        </div>
      </div>
    </div>
  );

  // portal 到 body 根打断层；容器不可用（极端 SSR）时回退就地渲染，保证功能不丢。
  return rootRef.current ? createPortal(dialog, rootRef.current) : dialog;
}

/* ------------------------------------------------------------------ */
/* 牌桌环形座位（第 18 轮，D-R16-1 / D-R16-2）                          */
/* ------------------------------------------------------------------ */

/**
 * 计算某个座位在牌桌椭圆上的百分比坐标。
 *
 * 约定（与大厅镜像统一为「你在底部」，D-R16-2）：
 *   - `slot = 0` 固定在椭圆**底部**（正中），即「你」；
 *   - 其余座位按 slot 递增沿椭圆**顺时针**均匀铺开（slot = (seat - yourSeat + n) % n）；
 *   - 返回百分比而非像素，保证 3–6 人各档都不溢出容器、不遮挡行动栏。
 */
export function tableSeatPos(slot: number, total: number): { x: number; y: number } {
  const n = Math.max(1, total);
  const step = 360 / n;
  // 屏幕坐标 y 轴朝下：90° = 正下方，角度递增为顺时针
  const deg = 90 + step * (slot % n);
  const rad = (deg * Math.PI) / 180;
  return {
    x: 50 + 40 * Math.cos(rad),
    y: 50 + 38 * Math.sin(rad),
  };
}

/** 供 style 直接使用的 CSS 变量形式。 */
export function tableSeatStyle(slot: number, total: number): React.CSSProperties {
  const { x, y } = tableSeatPos(slot, total);
  return { '--seat-x': `${x.toFixed(2)}%`, '--seat-y': `${y.toFixed(2)}%` } as React.CSSProperties;
}
